import { useState } from "react";
import '../../styles/AdminStyles/JoiningInstructions.css';
import EmailPreview from "./EmailPreview";
import { bindingLimit, EmailQuota, isAdminError, quotaResumesAt, Registration, SendProgress, SendRequest, Webinar } from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { DeliveriesByRegistration, hasJoiningInstructions, joiningSummary } from "../../webinars/emailStatus";
import { formatLongDate, formatResetTime, formatTimeRange, formatTimeUntil } from "../../webinars/format";
import { isSafeMeetingLink } from "../../webinars/shared/emailTemplates";

interface JoiningInstructionsPanelProps {
  webinar: Webinar;
  savedLink: string;
  /** Today's position against the daily email limit */
  quota: EmailQuota;
  /** This webinar's registrations */
  registrations: Registration[];
  deliveries: DeliveriesByRegistration;
  /** Registrations ticked in the attendee list, for "Send to selected" */
  selectedIds: string[];
  onClearSelection: () => void;
  onClose: () => void;
  notify: (message: string) => void;
}

type Job = { label: string; progress: SendProgress | null };

// One webinar's joining instructions: save the private link, preview, then email everyone who
// hasn't had it (or a selection), watch progress, and retry failures.
export default function JoiningInstructionsPanel({
  webinar, savedLink, quota, registrations, deliveries, selectedIds, onClearSelection, onClose, notify,
}: JoiningInstructionsPanelProps) {
  const [link, setLink] = useState(savedLink);
  const [linkError, setLinkError] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [result, setResult] = useState<{ tone: "ok" | "warning" | "error"; text: string } | null>(null);

  const summary = joiningSummary(registrations, deliveries);
  // "Retry failed" covers every failed email for this webinar, confirmations included. Bounces
  // aren't retried: the address itself is the problem
  const failedEmails = registrations
    .filter(r => r.status !== "cancelled")
    .flatMap(r => Object.values(deliveries[r.id] ?? {}))
    .filter(d => d?.status === "failed").length;
  const cancelled = webinar.status === "cancelled";
  const linkChanged = link.trim() !== savedLink;
  const hasLink = Boolean(savedLink) && !linkChanged;
  const selectedRegistered = registrations.filter(r => selectedIds.includes(r.id) && r.status === "registered");
  // Who among them already has the joining instructions (emailed, or sent by hand), named in the warning
  const selectedAlreadyHave = selectedRegistered.filter(r => hasJoiningInstructions(r, deliveries));
  const alreadyHaveNote = selectedAlreadyHave.length === 0
    ? "None of them has the joining instructions yet."
    : `${selectedAlreadyHave.length} of them already ${selectedAlreadyHave.length === 1 ? "has" : "have"} them and will get them again: ` +
      selectedAlreadyHave.slice(0, 5).map(r => `${r.firstName} ${r.lastName}`).join(", ") +
      (selectedAlreadyHave.length > 5 ? ` and ${selectedAlreadyHave.length - 5} more` : "") + ".";
  const sample = registrations.find(r => r.status === "registered") ?? { firstName: "Thandi", reference: "KMW-00000" };

  // The email limits (a day and a month), shared with registration confirmations
  const atLimit = quota.remaining === 0;
  const period = bindingLimit(quota);
  const limitName = period === "month" ? `monthly limit ${quota.monthlyLimit?.toLocaleString("en-ZA")}` : `daily limit ${quota.dailyLimit}`;
  const resumesAt = quotaResumesAt(quota);
  const resets = `${formatResetTime(resumesAt)} (${formatTimeUntil(resumesAt)})`;
  /** Added to the confirm question when a send won't fit in what's left */
  const limitNote = (count: number) =>
    quota.remaining !== null && count > quota.remaining
      ? `\n\nOnly ${quota.remaining} more email${quota.remaining === 1 ? "" : "s"} can go out ${period === "month" ? "this month" : "today"} (${limitName}). ` +
        `The other ${count - quota.remaining} wait in the queue: after the limit resets at ${resets}, press "Send waiting emails".`
      : "";

  const saveLink = async () => {
    setLinkError("");
    if (link.trim() && !isSafeMeetingLink(link)) {
      setLinkError("Paste the full joining link, starting with https://");
      return;
    }
    setSavingLink(true);
    try {
      await webinarRepository.saveMeetingLink(webinar.id, link);
      notify(link.trim() ? "Joining link saved" : "Joining link removed");
    } catch (error) {
      setLinkError(isAdminError(error) ? error.message : "Couldn't save the link. Try again.");
    } finally {
      setSavingLink(false);
    }
  };

  const run = async (label: string, action: (onProgress: (p: SendProgress) => void) => Promise<SendProgress>) => {
    setResult(null);
    setJob({ label, progress: null });
    try {
      const done = await action(progress => setJob({ label, progress }));
      const heldBack = done.quota?.remaining === 0 && done.remaining > 0;
      if (heldBack) {
        const which = bindingLimit(done.quota!) === "month" ? "Monthly" : "Daily";
        setResult({ tone: "warning", text: `Sent ${done.sent}${done.failed ? `, ${done.failed} failed (see the Emails column)` : ""}. ${which} email limit reached: ${done.remaining} ${done.remaining === 1 ? "is" : "are"} waiting. After the limit resets at ${formatResetTime(quotaResumesAt(done.quota!))}, press "Send waiting emails".` });
      } else if (!done.emailEnabled) {
        setResult({ tone: "warning", text: `${done.queued} email${done.queued === 1 ? " is" : "s are"} queued. Email sending isn't switched on yet (no email provider key on the server), so they'll go out once it is.` });
      } else if (done.failed > 0) {
        setResult({ tone: "warning", text: `Sent ${done.sent}. ${done.failed} failed: see the Emails column below, then use "Retry failed".` });
      } else if (done.remaining > 0) {
        setResult({ tone: "warning", text: `Sent ${done.sent}. ${done.remaining} still waiting (another send may be running); check back in a few minutes.` });
      } else {
        setResult({ tone: "ok", text: done.sent === 0 && done.queued === 0 ? "Nobody new to send to." : `Sent ${done.sent} email${done.sent === 1 ? "" : "s"}.` });
      }
      notify(`${label}: done`);
    } catch (error) {
      setResult({ tone: "error", text: isAdminError(error) ? error.message : "Something went wrong. Nobody gets an email twice, so it's safe to try again." });
    } finally {
      setJob(null);
    }
  };

  const send = (request: SendRequest, label: string, confirmText: string, count: number) => {
    if (!window.confirm(confirmText + limitNote(count))) return;
    run(label, onProgress => webinarRepository.sendJoiningInstructions(request, onProgress));
  };

  const busy = Boolean(job);
  const progress = job?.progress;
  const progressTotal = progress ? progress.sent + progress.failed + progress.remaining : 0;

  return (
    <section className="wa-panel wa-joining" id="joining" aria-labelledby="wa-joining-title">
      <div className="wa-panel-head">
        <div>
          <h2 id="wa-joining-title">Joining instructions</h2>
          <p className="wa-joining-webinar">
            <strong>{webinar.title}</strong> · {formatLongDate(webinar.startAt)}, {formatTimeRange(webinar.startAt, webinar.endAt)} SAST
            {webinar.meetingPlatform ? ` · ${webinar.meetingPlatform}` : ""}
          </p>
        </div>
        <button type="button" className="wa-btn" onClick={onClose} disabled={busy}>Close</button>
      </div>

      {cancelled ? (
        <p className="wa-warning" role="note">This webinar is cancelled, so joining instructions can't be sent. Tell the registered attendees directly.</p>
      ) : (
      <ol className="wa-steps">
        <li>
          <h3>Save the joining link</h3>
          <div className="wa-link-row">
            <input
              type="url"
              aria-label="Joining link"
              placeholder="https://teams.microsoft.com/l/meetup-join/… or another video link"
              value={link}
              onChange={e => { setLink(e.target.value); setLinkError(""); }}
            />
            <button type="button" className="wa-btn" onClick={saveLink} disabled={!linkChanged || savingLink}>
              {savingLink ? "Saving…" : "Save link"}
            </button>
          </div>
          {linkError && <p className="wa-form-error" role="alert">{linkError}</p>}
          <p className="wa-muted">
            A Microsoft Teams meeting link, or any other <code>https://</code> video link (Zoom, Google Meet, YouTube…).
            Private: only administrators can see it, and it's only emailed to registered attendees. Forwarded links can
            still be opened, so for Teams set <em>Who can bypass the lobby</em> to <em>Only organisers and
            co-organisers</em> in the meeting options and admit people from the lobby.
          </p>
        </li>

        <li>
          <h3>Check the email</h3>
          <p className="wa-muted">
            Attendees already have the registration confirmation. This sends them the same email again, with the
            joining link added.
          </p>
          <button type="button" className="wa-btn" onClick={() => setPreviewOpen(true)}>Preview email</button>
        </li>

        <li>
          <h3>Send</h3>
          <dl className="wa-joining-counts">
            <div><dt>Registered</dt><dd>{summary.registered}</dd></div>
            <div><dt>Sent</dt><dd>{summary.done}</dd></div>
            <div><dt>Not sent yet</dt><dd>{summary.none}</dd></div>
            <div className={summary.waiting ? "is-waiting" : ""}><dt>Waiting</dt><dd>{summary.waiting}</dd></div>
            <div className={summary.failed ? "is-failed" : ""}><dt>Failed</dt><dd>{summary.failed}</dd></div>
          </dl>

          {!hasLink && <p className="wa-warning">Save the joining link first.</p>}
          {quota.remaining !== null && (
            atLimit ? (
              <p className="wa-warning" role="status">
                {period === "month" ? "Monthly" : "Daily"} email limit reached ({period === "month" ? `${quota.monthlyLimit?.toLocaleString("en-ZA")} a month` : `${quota.dailyLimit} a day`}). Sending is paused until {resets}.
              </p>
            ) : (
              <p className="wa-muted">
                {quota.remaining} email{quota.remaining === 1 ? "" : "s"} can go out {period === "month" ? "this month" : "today"} ({limitName}) · resets at {resets}
              </p>
            )
          )}

          <div className="wa-row-actions">
            <button
              type="button"
              className="km-btn km-btn--primary"
              disabled={!hasLink || busy || atLimit || summary.none === 0}
              onClick={() => send(
                { webinarId: webinar.id },
                "Send joining instructions",
                `Email joining instructions to ${summary.none} attendee${summary.none === 1 ? "" : "s"} who haven't had them yet?`,
                summary.none,
              )}
            >
              {summary.none > 0 ? `Send to ${summary.none} not sent yet` : "Nobody new to send to"}
            </button>
            {selectedRegistered.length > 0 && (
              <button
                type="button"
                className="wa-btn wa-btn--quiet"
                disabled={!hasLink || busy || atLimit}
                onClick={() => send(
                  { webinarId: webinar.id, registrationIds: selectedRegistered.map(r => r.id), resend: true },
                  "Send to selected",
                  `Email joining instructions to the ${selectedRegistered.length} selected attendee${selectedRegistered.length === 1 ? "" : "s"}?

${alreadyHaveNote}`,
                  selectedRegistered.length,
                )}
              >
                Send to {selectedRegistered.length} selected
              </button>
            )}
            {failedEmails > 0 && (
              <button
                type="button"
                className="wa-btn wa-btn--quiet"
                disabled={busy || atLimit}
                onClick={() => run("Retry failed emails", onProgress => webinarRepository.retryFailedEmails(webinar.id, onProgress))}
              >
                Retry {failedEmails} failed email{failedEmails === 1 ? "" : "s"}
              </button>
            )}
            {summary.waiting > 0 && (
              <button
                type="button"
                className="wa-btn wa-btn--quiet"
                disabled={busy || atLimit}
                onClick={() => run("Send waiting emails", onProgress => webinarRepository.sendWaitingEmails(webinar.id, onProgress))}
              >
                Send {summary.waiting} waiting email{summary.waiting === 1 ? "" : "s"}
              </button>
            )}
            {summary.done > 0 && (
              <button
                type="button"
                className="wa-btn wa-btn--quiet"
                disabled={!hasLink || busy || atLimit}
                onClick={() => send(
                  { webinarId: webinar.id, resend: true },
                  "Resend to everyone",
                  `Send the joining instructions again to all ${summary.registered} registered attendees? Use this if the link changed.`,
                  summary.registered,
                )}
              >
                Resend to everyone
              </button>
            )}
          </div>
          {selectedRegistered.length > 0 && (
            <p className="wa-muted">
              {selectedRegistered.length} selected in the attendee list. {alreadyHaveNote}{" "}
              <button type="button" className="wa-link wa-link--inline" onClick={onClearSelection}>Clear selection</button>
            </p>
          )}

          {job && (
            <div className="wa-progress" role="status">
              <span>{job.label}… {progress ? `${progress.sent + progress.failed} of ${progressTotal}` : "starting"}</span>
              <progress max={progressTotal || 1} value={progress ? progress.sent + progress.failed : 0} />
            </div>
          )}
          {result && <p className={`wa-result wa-result--${result.tone}`} role={result.tone === "error" ? "alert" : "status"}>{result.text}</p>}

          <p className="wa-muted">
            Each attendee gets their own email; nobody sees anyone else's address. To message one person by WhatsApp
            instead, open their record in the attendee list.
          </p>
        </li>
      </ol>
      )}

      {previewOpen && (
        <EmailPreview webinar={webinar} meetingLink={link.trim()} sample={sample} onClose={() => setPreviewOpen(false)} />
      )}
    </section>
  );
}
